import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";

function formatRelative(ts: number): string {
  const sec = Math.round((Date.now() - ts) / 1000);
  if (sec < 60) return "épp most";
  if (sec < 3600) return `${Math.floor(sec / 60)} perce`;
  if (sec < 86400) return `${Math.floor(sec / 3600)} órája`;
  return new Date(ts).toLocaleString("hu-HU", { dateStyle: "short", timeStyle: "short" });
}

/**
 * Freshness label. When there is no successful query data yet, avoid the misleading
 * "még nincs szinkron" (implies a sync queue) — use explicit "no successful load" copy (A2/K2).
 */
export function LastSyncedLabel({ className }: { className?: string }) {
  const qc = useQueryClient();
  const online = useOnlineStatus();
  const [tick, setTick] = React.useState(0);

  React.useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 30_000);
    const unsub = qc.getQueryCache().subscribe(() => setTick((t) => t + 1));
    return () => {
      window.clearInterval(id);
      unsub();
    };
  }, [qc]);

  const latest = React.useMemo(() => {
    void tick;
    let max: number | null = null;
    for (const q of qc.getQueryCache().getAll()) {
      const t = q.state.dataUpdatedAt;
      if (t && (!max || t > max)) max = t;
    }
    return max;
  }, [qc, tick]);

  const status = online ? "Online" : "Offline";
  const freshness = latest
    ? `Utoljára frissítve: ${formatRelative(latest)}`
    : "még nem volt sikeres adatbetöltés";

  return (
    <p className={className ?? "text-center text-xs text-slate-500 dark:text-slate-400"} data-testid="last-synced-label">
      {status} · {freshness}
    </p>
  );
}
